import { useTranslations } from "next-intl";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";

function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** Icon-only light/dark switch: a pill with a thumb that slides under the active icon. */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const t = useTranslations("theme");
  const isDark = theme === "dark";

  const iconClass = (active: boolean) =>
    cn(
      "relative z-10 flex h-7 w-7 items-center justify-center transition-colors",
      active ? "text-[var(--brand-purple)]" : "text-white/70",
    );

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={t("toggle")}
      data-testid="theme-toggle"
      data-state={theme}
      onClick={toggle}
      className="relative inline-flex items-center rounded-full border border-white/30 bg-white/10 p-0.5 transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    >
      <span
        aria-hidden
        className={cn(
          "absolute left-0.5 top-0.5 h-7 w-7 rounded-full bg-white shadow transition-transform duration-200 motion-reduce:transition-none",
          isDark && "translate-x-7",
        )}
      />
      <span className={iconClass(!isDark)}>
        <SunIcon />
      </span>
      <span className={iconClass(isDark)}>
        <MoonIcon />
      </span>
    </button>
  );
}
